/**
 * 框节点 —— 纯逻辑。
 *
 * 框是**编辑器构造**：不参与求值、没有端口，只做三件事：
 *   1. 给节点分组（视觉上圈起来）
 *   2. 拖框时带着成员一起走
 *   3. 折叠/删除时有一个明确的边界
 *
 * ## 归属是「存下来的」，不是「每次算的」
 *
 * 判定用节点**中心点**是否落在框内 —— 但只在**拖放结束那一刻**判一次，
 * 结果存进 `frameId`。
 *
 * 如果每次渲染都按几何重新算，会有两个坏结果：
 *   · 节点跨在框边界上时会来回抖（算一半在里面）
 *   · 你把框挪开一点，里面的节点就「掉出去」了 —— 而用户只是想挪框
 *
 * ## 拖拽传播要用「起点 + 增量」，不能累加增量
 *
 * ```ts
 * // ✓ 用起点快照 + 总增量
 * member.position = start[member.id] + (frame.position - frame.start)
 * // ✗ 每次 nodeDrag 都把增量累加到成员当前位置上 —— 浮点误差会累积
 * ```
 */

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

export interface Box extends Rect {
  id: string
}

/* ⚠️ 和 `cutGeometry` 重复定义过 —— 已下沉到 `types/geometry.ts`（见那里的说明） */
import type { Point } from '../types/geometry'
export type { Point } from '../types/geometry'

/**
 * 最小尺寸 / 默认尺寸都从 types/node.ts 引 ——
 * 库里的 NpNode 也要用同一份，各写一遍迟早对不上。
 */
export {
  MIN_FRAME_WIDTH as FRAME_MIN_WIDTH,
  MIN_FRAME_HEIGHT as FRAME_MIN_HEIGHT,
} from '../types/node'
import { MIN_FRAME_HEIGHT, MIN_FRAME_WIDTH } from '../types/node'

/** 从一堆节点算框的尺寸时留的边距 */
export const FRAME_PADDING = 24
/** 顶部留给标题栏的高度 */
export const FRAME_HEADER = 26

export function containsPoint(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  )
}

export function centerOf(box: Box): Point {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 }
}

/**
 * 哪个框装着这个节点。
 *
 * 多个框重叠时取**最后一个**（数组靠后 = 后画的 = 视觉上在上层）——
 * 跟用户看到的一致。
 */
export function frameFor(box: Box, frames: readonly Box[]): string | null {
  const center = centerOf(box)
  for (let i = frames.length - 1; i >= 0; i -= 1) {
    const frame = frames[i]
    if (frame && frame.id !== box.id && containsPoint(frame, center)) return frame.id
  }
  return null
}

/** 一个框要罩住这些节点时，该占多大（含内边距和标题栏） */
export function boundsFor(
  boxes: readonly Box[],
  options: { padding?: number; header?: number } = {},
): Rect | null {
  if (boxes.length === 0) return null

  const padding = options.padding ?? FRAME_PADDING
  const header = options.header ?? FRAME_HEADER

  const left = Math.min(...boxes.map((b) => b.x))
  const top = Math.min(...boxes.map((b) => b.y))
  const right = Math.max(...boxes.map((b) => b.x + b.width))
  const bottom = Math.max(...boxes.map((b) => b.y + b.height))

  return {
    x: left - padding,
    // 往上多留标题栏的高度，免得框住之后标题压住成员
    y: top - padding - header,
    width: Math.max(MIN_FRAME_WIDTH, right - left + padding * 2),
    height: Math.max(MIN_FRAME_HEIGHT, bottom - top + padding * 2 + header),
  }
}

/**
 * 框移动后，成员应该在哪。
 *
 * 传入的是**拖拽开始时**的成员位置快照和框的总位移 ——
 * 见文件头「不能累加增量」那条。
 */
export function moveMembers(
  startPositions: readonly { id: string; x: number; y: number }[],
  delta: Point,
): { id: string; x: number; y: number }[] {
  return startPositions.map((member) => ({
    id: member.id,
    x: member.x + delta.x,
    y: member.y + delta.y,
  }))
}

/* ══════════════════════════════════════════════════════════════
   删除语义
   ══════════════════════════════════════════════════════════════ */

/**
 * 删框的三种语义。
 *
 *   'frame-only'     只删框，成员留在原地（它们只是「不再属于任何框」）
 *   'with-children'  连成员一起删
 */
export type FrameDeleteMode = 'frame-only' | 'with-children'

export interface DeletionPlan {
  /** 要删掉的节点 id */
  deleted: string[]
  /** 要「脱离框」的节点 —— 只删框时需要把它们的 frameId 清掉 */
  orphaned: string[]
}

/**
 * 把「选中了这些」展开成实际要删哪些。
 *
 * ⚠️ 只删框时**必须**把成员的 frameId 清掉。
 * 不清的话会留下指向已删框的引用 —— 之后新建的框如果 id 撞上（或直接
 * 按 frameId 找框）就会把不相干的节点认领走，而且是静默的。
 */
export function planFrameDeletion(
  selected: readonly string[],
  mode: FrameDeleteMode,
  isFrame: (id: string) => boolean,
  childrenOf: (frameId: string) => readonly string[],
): DeletionPlan {
  const deleted = new Set<string>()
  const orphaned = new Set<string>()

  for (const id of selected) {
    deleted.add(id)
    if (!isFrame(id)) continue

    for (const childId of childrenOf(id)) {
      if (mode === 'with-children') {
        deleted.add(childId)
      } else {
        orphaned.add(childId)
      }
    }
  }

  // 同一个节点既被直接选中又被框带着删 → 只算「删」，不算「脱离」
  for (const id of deleted) orphaned.delete(id)

  return { deleted: [...deleted], orphaned: [...orphaned] }
}
